import React, { useMemo, useState } from "react";
import { classNames, formatDueLabel, formatDateTime } from "./utils";
import { BUCKET_LABELS, BUCKET_ORDER } from "../hooks/useSmartView";

function Bucket({ label, items, children, defaultOpen = true, accent }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!items?.length) return null;
  return (
    <div className="border-b border-zinc-200 last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-5 py-2 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:bg-zinc-50"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${accent}`} />
        <span>{label}</span>
        <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">
          {items.length}
        </span>
        <span className="ml-auto text-[10px] text-zinc-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open ? <div>{children}</div> : null}
    </div>
  );
}

const BUCKET_ACCENT = {
  overdue: "bg-red-500",
  today: "bg-amber-500",
  tomorrow: "bg-sky-500",
  this_week: "bg-indigo-500",
  later: "bg-zinc-400",
};

export default function QueueView({
  grouped,
  total,
  selectedQueueId,
  onSelectQueue,
  onQueueAction,
  gmailConnected,
  onOpenLead,
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());

  const allItems = useMemo(() => {
    const out = [];
    for (const key of BUCKET_ORDER) {
      out.push(...(grouped?.[key] || []));
    }
    return out;
  }, [grouped]);

  const preview = useMemo(() => {
    if (!allItems.length) return null;
    return (
      allItems.find((row) => row.queueItem._id === selectedQueueId) || allItems[0]
    );
  }, [allItems, selectedQueueId]);

  const toggleOne = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const runAction = async (action, ids) => {
    await onQueueAction?.(action, ids);
    setSelectedIds(new Set());
  };

  if (!total) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="text-base font-semibold text-zinc-900">No pending outreach</p>
        <p className="mt-1 max-w-sm text-sm text-zinc-500">
          New enrollments appear here as they become due. You can send, skip, or refresh any pending message before it goes out.
        </p>
      </div>
    );
  }

  return (
    <div className="grid h-full grid-cols-[1.1fr,1fr]">
      <div className="flex flex-col border-r border-zinc-200">
        {selectedIds.size ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-indigo-100 bg-indigo-50/60 px-5 py-2 text-sm">
            <span className="font-medium text-indigo-900">{selectedIds.size} selected</span>
            <span className="h-4 w-px bg-indigo-200" />
            <button
              onClick={() => runAction("send", selectedArray)}
              disabled={!gmailConnected}
              className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
            >
              Send selected
            </button>
            <button
              onClick={() => runAction("skip", selectedArray)}
              className="rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Skip selected
            </button>
            <button
              onClick={() => runAction("regenerate", selectedArray)}
              className="rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Regenerate
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto text-xs font-medium text-indigo-700 hover:underline"
            >
              Clear selection
            </button>
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto">
          {BUCKET_ORDER.map((key) => (
            <Bucket
              key={key}
              label={BUCKET_LABELS[key]}
              items={grouped?.[key] || []}
              accent={BUCKET_ACCENT[key]}
              defaultOpen={key === "overdue" || key === "today"}
            >
              {(grouped?.[key] || []).map((row) => {
                const id = row.queueItem._id;
                const selected = selectedIds.has(id);
                const isActive = preview?.queueItem._id === id;
                return (
                  <div
                    key={id}
                    onClick={() => onSelectQueue?.(id)}
                    className={classNames(
                      "grid cursor-pointer grid-cols-[32px,1.3fr,0.7fr,0.8fr,auto] items-center gap-2 border-b border-zinc-100 px-5 py-2 transition-colors",
                      isActive ? "bg-indigo-50/60" : "hover:bg-zinc-50"
                    )}
                  >
                    <label onClick={(e) => e.stopPropagation()} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleOne(id)}
                        className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
                      />
                    </label>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-zinc-900">
                        {row.lead?.company_name || row.queueItem.to?.[0] || "Lead"}
                      </p>
                      <p className="truncate text-xs text-zinc-500">
                        {row.queueItem.renderedSubject}
                      </p>
                    </div>
                    <div className="text-xs text-zinc-600">
                      Step {row.queueItem.stepIndex + 1}
                    </div>
                    <div
                      className={classNames(
                        "text-xs tabular-nums",
                        key === "overdue" ? "text-red-600 font-medium" : "text-zinc-600"
                      )}
                    >
                      {formatDueLabel(row.queueItem.dueAt)}
                    </div>
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => runAction("regenerate", [id])}
                        className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
                      >
                        Refresh
                      </button>
                      <button
                        onClick={() => runAction("skip", [id])}
                        className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
                      >
                        Skip
                      </button>
                      <button
                        onClick={() => runAction("send", [id])}
                        disabled={!gmailConnected}
                        className="rounded-md bg-zinc-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                );
              })}
            </Bucket>
          ))}
        </div>
      </div>

      <div className="flex h-full flex-col overflow-hidden">
        {preview ? (
          <>
            <div className="border-b border-zinc-200 bg-white px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-zinc-900">
                    {preview.lead?.company_name || "Lead"}
                  </p>
                  <p className="truncate text-xs text-zinc-500">
                    To {preview.queueItem.to?.join(", ") || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() =>
                      preview.lead
                        ? onOpenLead?.({ lead: preview.lead, campaignLead: preview.campaignLead })
                        : null
                    }
                    className="rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Open lead
                  </button>
                  <button
                    onClick={() => runAction("regenerate", [preview.queueItem._id])}
                    className="rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Refresh
                  </button>
                  <button
                    onClick={() => runAction("skip", [preview.queueItem._id])}
                    className="rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Skip
                  </button>
                  <button
                    onClick={() => runAction("send", [preview.queueItem._id])}
                    disabled={!gmailConnected}
                    className="rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
                  >
                    Send now
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-zinc-500">
                <span>Step {preview.queueItem.stepIndex + 1}</span>
                <span>·</span>
                <span>Due {formatDateTime(preview.queueItem.dueAt)}</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-zinc-50 px-5 py-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Subject</p>
              <p className="mt-1 text-sm font-medium text-zinc-900">
                {preview.queueItem.renderedSubject}
              </p>
              <div className="mt-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-card">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Body
                </p>
                <div
                  className="prose prose-sm mt-3 max-w-none text-zinc-700"
                  dangerouslySetInnerHTML={{ __html: preview.queueItem.renderedHtml || "" }}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-zinc-500">
            Select a queue item to preview
          </div>
        )}
      </div>
    </div>
  );
}
